package features.home.ui.components

import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import kotlinx.html.dom.append
import AppState
import features.attendance.helpers.AttendanceHelpers
import shared.ui.ModalManager

object TeamSelector {
    fun render(parent: DIV) {
        val userId = AppState.currentUser?.id ?: return
        
        // AppState.teams already contains only teams where user is a member
        val userTeams = AppState.teams
        
        if (userTeams.isEmpty()) return
        
        parent.div("bg-slate-900/50 p-6 rounded-2xl border border-slate-700/50 mb-8") {
            // Header
            h3("font-bold text-slate-300 mb-4 text-sm uppercase tracking-wider") {
                +"Your Teams"
            }
            
            // Team List
            div("space-y-4") {
                userTeams.forEach { team ->
                    val teamId = team.id
                    val shift = AttendanceHelpers.getUserShiftForToday(userId, teamId)
                    val (startTime, endTime) = AttendanceHelpers.getWorkHoursForUser(userId, teamId)
                    val isOff = shift?.code == "OFF"
                    
                    // Team Item
                    div("flex items-center justify-between p-4 rounded-2xl bg-slate-800/40 border border-slate-700/50 hover:border-slate-600 transition") {
                        // Left: Team Info
                        div("flex items-center gap-4") {
                            // Team Icon
                            div("w-10 h-10 rounded-full bg-gradient-to-br from-brand-cyan to-blue-500 flex items-center justify-center text-white font-bold shadow-lg shadow-cyan-900/20") {
                                +getTeamEmoji(team.name)
                            }
                            
                            // Team Details
                            div {
                                h4("font-bold") {
                                    +team.name
                                }
                                div("flex items-center gap-2 text-xs text-slate-400") {
                                    if (isOff) {
                                        span("bg-slate-700 px-1.5 rounded") {
                                            +"Day Off"
                                        }
                                    } else {
                                        span("bg-slate-700 px-1.5 rounded") {
                                            +"$startTime - $endTime"
                                        }
                                    }
                                }
                            }
                        }
                        
                        // Right: Clock In/Out Button (async loaded)
                        if (!isOff) {
                            // Create container with unique ID for this team
                            val buttonContainerId = "team-button-$teamId"
                            div {
                                id = buttonContainerId
                                
                                // Show loading state initially
                                div("bg-slate-700/50 text-slate-400 text-sm font-bold px-5 py-2.5 rounded-xl flex items-center gap-2 animate-pulse") {
                                    span { +"⏳" }
                                    +"Loading..."
                                }
                            }
                            
                            // Query attendance status asynchronously from Firestore
                            console.log("🔍 Querying attendance for team: ${team.name}")
                            features.attendance.data.AttendanceRepository.getTodayAttendance(teamId, userId)
                                .then { record ->
                                    val attendanceStatus = when {
                                        record == null -> "not_clocked_in"
                                        record.checkOut != "-" -> "clocked_out"
                                        record.checkIn != "-" -> "clocked_in"
                                        else -> "not_clocked_in"
                                    }
                                    
                                    console.log("✅ Query result for ${team.name}: $attendanceStatus")
                                    
                                    // Update button container with actual button using innerHTML
                                    val container = kotlinx.browser.document.getElementById(buttonContainerId)
                                    if (container != null) {
                                        when (attendanceStatus) {
                                            "not_clocked_in" -> {
                                                // Set currentTeam before rendering button
                                                AppState.currentTeam = team
                                                container.innerHTML = """
                                                    <button class="bg-brand-cyan hover:bg-brand-green text-white text-sm font-bold px-5 py-2.5 rounded-xl transition shadow-lg shadow-brand-cyan/20 active:transform active:scale-95 flex items-center gap-2" onclick="window.ModalManager.show('clock-in-modal')">
                                                        <span>Clock In</span>
                                                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                                                    </button>
                                                """.trimIndent()
                                            }
                                            "clocked_in" -> {
                                                // Set currentTeam before rendering button
                                                AppState.currentTeam = team
                                                container.innerHTML = """
                                                    <button class="bg-violet-500 hover:bg-violet-600 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition shadow-lg shadow-violet-500/20 active:transform active:scale-95 flex items-center gap-2" onclick="window.isClockOutMode = true; window.ModalManager.show('clock-in-modal')">
                                                        <span>Clock Out</span>
                                                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                                                    </button>
                                                """.trimIndent()
                                            }
                                            "clocked_out" -> {
                                                container.innerHTML = """
                                                    <div class="bg-emerald-500/20 text-emerald-400 text-sm font-bold px-5 py-2.5 rounded-xl flex items-center gap-2">
                                                        <span>✓</span>
                                                        <span>Complete</span>
                                                    </div>
                                                """.trimIndent()
                                            }
                                        }
                                    }
                                }
                                .catch { error ->
                                    console.error("❌ Failed to query attendance:", error)
                                    // Show error state
                                    val container = kotlinx.browser.document.getElementById(buttonContainerId)
                                    if (container != null) {
                                        container.innerHTML = """<div class="bg-red-500/20 text-red-400 text-sm font-bold px-5 py-2.5 rounded-xl">Error</div>"""
                                    }
                                }
                        } else {
                            // Day Off Badge
                            div("bg-slate-700/50 text-slate-400 text-sm font-bold px-5 py-2.5 rounded-xl cursor-not-allowed") {
                                +"Day Off"
                            }
                        }
                    }
                }
            }
            
            // Info Tip
            if (userTeams.size > 1) {
                div("mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20") {
                    p("text-xs text-blue-300") {
                        unsafe {
                            raw("""💡 <strong>Tip:</strong> You can clock in to multiple teams separately if you have overlapping schedules""")
                        }
                    }
                }
            }
        }
    }
    
    private fun getTeamEmoji(teamName: String): String {
        return when {
            teamName.contains("engineering", ignoreCase = true) || 
            teamName.contains("developer", ignoreCase = true) || 
            teamName.contains("tech", ignoreCase = true) -> "🏢"
            
            teamName.contains("design", ignoreCase = true) || 
            teamName.contains("creative", ignoreCase = true) || 
            teamName.contains("ui", ignoreCase = true) -> "🎨"
            
            teamName.contains("marketing", ignoreCase = true) || 
            teamName.contains("sales", ignoreCase = true) -> "📊"
            
            teamName.contains("hr", ignoreCase = true) || 
            teamName.contains("people", ignoreCase = true) -> "👥"
            
            teamName.contains("finance", ignoreCase = true) || 
            teamName.contains("accounting", ignoreCase = true) -> "💰"
            
            else -> "👥"
        }
    }
}
